import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Smartphone, Ticket, Shield, WifiOff, Timer, RefreshCw, Loader2, FileText,
} from 'lucide-react';
import { api } from '../../lib/api';

/* Historique complet des échanges : qui a parlé à Oliv'IA, ce qui a été demandé,
   ce qui a été répondu, avec quel modèle et quelle version du persona. */

const METHOD_ICON = { totp: Smartphone, coupon: Ticket, admin: Shield };
const METHOD_LABEL = { totp: 'authenticator', coupon: 'coupon', admin: 'test admin' };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

export default function AdminConversations() {
  const [conversations, setConversations] = useState([]);
  const [personaVersion, setPersonaVersion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/conversations');
      setConversations(data.conversations);
      setPersonaVersion(data.persona_version);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (conv) => {
    setSelected(conv.id);
    setDetailLoading(true);
    try {
      setDetail(await api.get(`/api/admin/conversations/${conv.id}`));
    } catch (e) {
      setErr(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const togglePrompt = async () => {
    if (prompt) { setPrompt(null); return; }
    try {
      const persona = await api.get('/api/admin/persona');
      setPrompt(persona.compiled_prompt);
    } catch (e) {
      setErr(e.message);
    }
  };

  const totalQuestions = conversations.reduce((s, c) => s + c.questions_count, 0);
  const guestConvs = conversations.filter((c) => !c.admin);

  return (
    <div className="admin-grid">
      <div className="panel span2">
        <div className="console-head">
          <h3 className="panel-h"><MessageSquare size={17} /> Historique des échanges</h3>
          <div className="console-actions">
            <button className="btn-ghost" onClick={togglePrompt}>
              <FileText size={15} /> {prompt ? 'Masquer le prompt' : 'Voir le prompt en vigueur'}
            </button>
            <button className="btn-ghost" onClick={load} disabled={loading}>
              {loading ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />} Actualiser
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-tile"><div className="stat-val">{guestConvs.length}</div><div className="stat-lab">sessions invités</div></div>
          <div className="stat-tile"><div className="stat-val">{conversations.length - guestConvs.length}</div><div className="stat-lab">sessions de test admin</div></div>
          <div className="stat-tile"><div className="stat-val">{totalQuestions}</div><div className="stat-lab">questions posées</div></div>
          <div className="stat-tile"><div className="stat-val">v{personaVersion ?? '—'}</div><div className="stat-lab">version du persona</div></div>
        </div>

        {prompt && (
          <div className="prompt-view">
            <div className="secret-label">Prompt système compilé — persona v{personaVersion}</div>
            <pre>{prompt}</pre>
          </div>
        )}

        {err && <div className="test err">{err}</div>}

        <div className="conv-layout">
          <div className="conv-list">
            {loading && <div className="empty">Chargement…</div>}
            {!loading && conversations.length === 0 && <div className="empty">Aucun échange enregistré pour l'instant.</div>}
            {conversations.map((c) => {
              const Icon = METHOD_ICON[c.auth_method] || Smartphone;
              return (
                <button
                  key={c.id}
                  className={`conv-row${selected === c.id ? ' active' : ''}${c.admin ? ' admin' : ''}`}
                  onClick={() => open(c)}
                >
                  <span className="conv-icon"><Icon size={14} /></span>
                  <span className="conv-main">
                    <span className="conv-title">Session #{c.id} · {METHOD_LABEL[c.auth_method] || c.auth_method}</span>
                    <span className="conv-meta">{c.questions_count} question{c.questions_count > 1 ? 's' : ''} · {fmtDate(c.last_seen_at || c.created_at)}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="conv-detail">
            {detailLoading && <div className="empty"><Loader2 size={16} className="spin" /></div>}
            {!detailLoading && !detail && <div className="empty">Sélectionnez une session pour lire l'échange.</div>}
            {!detailLoading && detail && (
              <>
                <div className="conv-detail-head">
                  <b>Session #{detail.id}</b>
                  <span>{METHOD_LABEL[detail.auth_method] || detail.auth_method}</span>
                  {detail.device_hint && <span className="conv-device" title={detail.device_hint}>{detail.device_hint}</span>}
                  <span>ouverte le {fmtDate(detail.created_at)}</span>
                </div>
                {detail.messages.length === 0 && <div className="empty">Session ouverte, aucune question posée.</div>}
                {detail.messages.map((m) => (
                  <div key={m.id} className={`conv-msg ${m.role}`}>
                    <div className="conv-bubble">
                      {m.content}
                      <div className="conv-msg-meta">
                        {fmtDate(m.created_at)}
                        {m.persona_version && ` · persona v${m.persona_version}`}
                        {m.model && ` · ${m.model}`}
                        {m.latency_ms != null && ` · ${m.latency_ms} ms`}
                        {m.offline && <> · <WifiOff size={10} /> hors ligne</>}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="sec-warn slim">
          <Timer size={15} />
          Journalisation volontairement minimale : aucune donnée personnelle des invités hors indication
          d'appareil anonyme. La purge RGPD (onglet Réglages) efface l'intégralité de cet historique.
        </div>
      </div>
    </div>
  );
}
